import { nanoid } from "@reduxjs/toolkit";
import classNames from "classnames";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";

import useOnClickOutside from "../../../hooks/useClickOutside";
import LoadingIndicator from "../LoadingIndicator";
import SvgIcon from "../SvgIcon";

import styles from "./index.module.scss";

interface ISearchInputProps {
  className?: string;
  value: string;
  valueKey: string;
  displayKey: string;
  results: Array<Record<string, string>> | null | [];
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  isLoading?: boolean;
  isRequired?: boolean;
  readOnly?: boolean;
  isMultiple?: boolean;
  showError?: boolean;
  onClick?: () => void;
  changes?: Array<Record<string, string>>;
  onChosenChange?: ({
    items,
    value,
    valueKey,
  }: {
    items?: Array<Record<string, string>>;
    value: Record<string, string>;
    valueKey: string;
  }) => void;
  onChange?: ({
    value,
    valueKey,
  }: {
    value: string;
    valueKey?: string;
  }) => void;
}

function SearchInput({
  value,
  displayKey,
  results,
  isLoading,
  onClick,
  onChosenChange,
  changes,
  className = "",
  valueKey = "",
  label = "",
  placeholder = "",
  errorMessage = "",
  isRequired = false,
  isMultiple = false,
  showError = true,
  readOnly = false,
  onChange = () => {},
}: ISearchInputProps) {
  const [internalValue, setInternalValue] = useState(value);
  const [internalResults, setInternalResults] = useState(results);
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [chosenItems, setChosenItems] = useState<Array<Record<string, string>>>(
    [],
  );

  useEffect(() => {
    setChosenItems(changes ?? []);
  }, [changes]);

  const id = useMemo(nanoid, []);

  const outsideRef = useRef(null);

  const timeoutIdRef: MutableRefObject<ReturnType<typeof setTimeout> | null> =
    useRef(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(timeoutIdRef.current as ReturnType<typeof setTimeout>);
    setInternalValue(e.target.value);

    timeoutIdRef.current = setTimeout(() => {
      if (onChange) {
        onChange({ value: e.target.value, valueKey });
      }
    }, 500);
  };

  const handleChose = (item: Record<string, string>) => () => {
    if (isMultiple) {
      const newChosenItems = [...chosenItems, item];

      setChosenItems(newChosenItems);

      if (onChosenChange) {
        onChosenChange({ items: newChosenItems, value: item, valueKey });
      }
    } else if (onChosenChange) {
      onChosenChange({ value: item, valueKey });
      setIsResultsVisible(false);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleFocus = () => {
    setIsResultsVisible(true);
  };

  const handleClickOutside = () => {
    if (isLoading) {
      return;
    }

    setIsResultsVisible(false);
  };

  const handleRemoveChosen = (item: Record<string, string>) => () => {
    const newChosen = chosenItems.filter(
      (el) => el[displayKey] !== item[displayKey],
    );

    setChosenItems(newChosen);

    if (onChosenChange) {
      onChosenChange({ items: newChosen, value: item, valueKey });
    }
  };

  useOnClickOutside(outsideRef, handleClickOutside);

  useEffect(() => {
    setInternalValue(value);
  }, [value, chosenItems]);

  useEffect(() => {
    if (isLoading) {
      setIsResultsVisible(true);
    }
  }, [isLoading, results]);

  useEffect(() => {
    const chosenDisplayKeys = chosenItems.map((el) => el[displayKey]);

    const filteredResults = (results ?? []).filter(
      (el) => !chosenDisplayKeys.includes(el[displayKey]),
    );

    setInternalResults(filteredResults);
  }, [results, chosenItems]);

  return (
    <div className={classNames(styles.container, className)}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {isRequired && <span className={styles.asterisk}>*</span>}
        </label>
      )}

      <div className={styles.fieldContainer}>
        <input
          id={id}
          className={classNames(styles.field, {
            [styles.field_error]: !!errorMessage,
          })}
          value={internalValue}
          placeholder={placeholder}
          readOnly={readOnly}
          onClick={handleClick}
          onChange={handleChange}
          onFocus={handleFocus}
        />

        <div
          className={classNames(styles.chosen, {
            [styles.chosen_visible]: chosenItems.length,
          })}>
          {chosenItems.map((el) => {
            return (
              <div key={nanoid()} className={styles.chosenItem}>
                <p>{el[displayKey]}</p>
                <SvgIcon
                  type="FiPlus"
                  className={styles.closeIcon}
                  onClick={handleRemoveChosen(el)}
                />
              </div>
            );
          })}
        </div>

        <div
          ref={outsideRef}
          className={classNames(styles.results, {
            [styles.results_visible]: isResultsVisible,
          })}>
          {isLoading ? (
            <LoadingIndicator
              className={styles.loader}
              width={24}
              height={24}
            />
          ) : internalResults?.length ? (
            internalResults.map((el) => {
              return (
                <div
                  key={nanoid()}
                  className={styles.resultsItem}
                  onClick={handleChose(el)}>
                  <p>{el[displayKey]}</p>
                </div>
              );
            })
          ) : (
            <></>
          )}
        </div>
      </div>

      {showError ? <span className={styles.error}>{errorMessage}</span> : null}
    </div>
  );
}

export default SearchInput;
